/**
 * Tenant Configuration System
 * Détecte le sous-domaine et configure l'application en conséquence
 */

import { getHostname, getPrimaryDomain } from './domainUtils';

// Types de tenants
export const TENANT_TYPES = {
  CUSTOMER: 'customer',
  REFERRAL: 'referral',
  PLATFORM: 'platform',
  HALITRADE: 'halitrade',
  HALIAGENT: 'haliagent',
  HALILAB: 'halilab',
  HALIFUND: 'halifund'
};

const VALID_TENANT_TYPES = new Set(Object.values(TENANT_TYPES));
const TENANT_STORAGE_KEY = 'multiTenant.currentTenant';
const isBrowserEnvironment = typeof window !== 'undefined';
let cachedTenantType = null;

const REFERRAL_ALIASES = new Set(['referral', 'referrer', 'referrals', 'partner', 'referral_partner']);
const HALITRADE_ALIASES = new Set(['halitrade', 'halitrade_user']);
const HALIAGENT_ALIASES = new Set(['haliagent', 'haliagent_user', 'halitrade-agent', 'halitrade_agent', 'agent']);
const HALILAB_ALIASES = new Set(['halilab', 'halilab_user', 'halilab-developer', 'halilab_developer']);
const HALIFUND_ALIASES = new Set(['halifund', 'investor', 'funder', 'funding_partner']);
const PLATFORM_PATH_ALIASES = ['/factor-tools', '/analytics'];
const CATEGORY_ALIASES = {
  [TENANT_TYPES.PLATFORM]: ['platform_user', 'platform', 'platform_admin', 'platform-manager', 'platform_manager'],
  [TENANT_TYPES.REFERRAL]: ['referral', 'referrer', 'referral_partner', 'partner', 'referrals'],
  [TENANT_TYPES.CUSTOMER]: ['customer', 'client'],
  [TENANT_TYPES.HALITRADE]: ['halitrade', 'halitrade_user'],
  [TENANT_TYPES.HALIAGENT]: ['haliagent', 'haliagent_user', 'halitrade-agent', 'halitrade_agent', 'agent'],
  [TENANT_TYPES.HALILAB]: ['halilab', 'halilab_user', 'halilab-developer', 'halilab_developer'],
  [TENANT_TYPES.HALIFUND]: ['halifund', 'investor', 'funder', 'funding_partner']
};

const isValidTenantType = (value) => VALID_TENANT_TYPES.has(value);

const rememberTenant = (tenantType) => {
  cachedTenantType = tenantType;

  if (isBrowserEnvironment && window.sessionStorage) {
    try {
      window.sessionStorage.setItem(TENANT_STORAGE_KEY, tenantType);
    } catch (error) {
      // Ignore storage persistence errors (private mode, quotas, etc.)
    }
  }

  return tenantType;
};

const getRememberedTenant = () => {
  if (isValidTenantType(cachedTenantType)) {
    return cachedTenantType;
  }

  if (isBrowserEnvironment && window.sessionStorage) {
    try {
      const storedTenant = window.sessionStorage.getItem(TENANT_STORAGE_KEY);
      if (isValidTenantType(storedTenant)) {
        cachedTenantType = storedTenant;
        return storedTenant;
      }
    } catch (error) {
      // Ignore retrieval errors and fall back to defaults
    }
  }

  return null;
};

// Configuration par tenant
export const TENANT_CONFIG = {
  [TENANT_TYPES.CUSTOMER]: {
    subdomain: 'ai.app.customer',
    name: 'HaliTrade',
    defaultRoute: '/customer/dashboard',
    allowedCategories: ['customer'],
    theme: {
      primary: '#DC2626', // Red
      secondary: '#EF4444', // Light Red
      accent: '#B91C1C', // Dark Red
      logo: '/assets/halisoft-monogram.svg',
      brandName: 'HaliTrade',
      tagline: 'powered by HaliSoft'
    },
    features: {
      canViewOwnData: true,
      canEditOwnData: true,
      canSubmitForApproval: true,
      canViewApprovalStatus: true,
      canAccessFactoring: true,
      canAccessSupplyChain: true,
      canAccessDataExplorer: false,
      canManageUsers: false,
      // Tabs visible on invoice financing detail page
      marketplaceDetailTabs: ['overview', 'shipping', 'documents']
    }
  },
  [TENANT_TYPES.REFERRAL]: {
    subdomain: 'app.referral',
    name: 'Referral Portal',
    defaultRoute: '/referral/dashboard',
    allowedCategories: ['referral'],
    theme: {
      primary: '#10B981', // Green
      secondary: '#3B82F6',
      logo: '/assets/referral-logo.png'
    },
    features: {
      canViewReferrals: true,
      canAddReferrals: true,
      canViewCommissions: true,
      canAccessDataExplorer: false,
      canManageUsers: false
    }
  },
  [TENANT_TYPES.PLATFORM]: {
    subdomain: 'ai.app.platform',
    name: 'HaliTrade Platform',
    defaultRoute: '/platform/dashboard',
    allowedCategories: ['platform_user'],
    theme: {
      primary: '#DC2626', // Red
      secondary: '#EF4444', // Light Red
      logo: '/assets/halisoft-monogram.svg',
      brandName: 'HaliTrade',
      tagline: 'Platform'
    },
    features: {
      canValidateData: true,
      canChangeStatus: true,
      canViewAllData: true,
      canAccessDataExplorer: true,
      canManageUsers: true,
      canViewAnalytics: true,
      // Tabs visible on invoice financing detail page (platform sees all)
      marketplaceDetailTabs: ['overview', 'shipping', 'documents', 'risk', 'financing']
    }
  },
  [TENANT_TYPES.HALITRADE]: {
    subdomain: 'halitrade',
    name: 'HaliTrade',
    defaultRoute: '/customer/dashboard',
    allowedCategories: ['customer', 'halitrade', 'halitrade_user', 'platform_user', 'platform_admin'],
    theme: {
      primary: '#DC2626', // Red (HaliTrade brand color)
      secondary: '#EF4444', // Light Red
      accent: '#B91C1C', // Dark Red
      logo: '/assets/halisoft-monogram.svg',
      brandName: 'HaliTrade',
      brandNamePrefix: 'Hali',
      brandNameHighlight: 'Trade',
      tagline: 'powered by HaliSoft'
    },
    features: {
      canViewOwnData: true,
      canEditOwnData: true,
      canSubmitForApproval: true,
      canViewApprovalStatus: true,
      canAccessFactoring: true,
      canAccessSupplyChain: true,
      canAccessDataExplorer: false,
      canManageUsers: false,
      // Tabs visible on invoice financing detail page (no risk/financing for customers)
      marketplaceDetailTabs: ['overview', 'shipping', 'documents']
    }
  },
  [TENANT_TYPES.HALIAGENT]: {
    subdomain: 'haliagent',
    name: 'HaliAgent',
    defaultRoute: '/haliagent/dashboard',
    allowedCategories: ['customer', 'halitrade', 'halitrade_user', 'haliagent', 'haliagent_user'],
    theme: {
      primary: '#F6B800', // Yellow/Gold
      secondary: '#FBBF24', // Light Yellow
      accent: '#FCD34D', // Lighter Yellow
      logo: '/assets/halitrade-logo.svg',
      brandName: 'HaliAgent',
      brandNamePrefix: 'Hali',
      brandNameHighlight: 'Agent',
      tagline: 'powered by HaliSoft'
    },
    features: {
      canViewOwnData: true,
      canEditOwnData: true,
      canSubmitForApproval: true,
      canViewApprovalStatus: true,
      canAccessFactoring: true,
      canAccessSupplyChain: true,
      canAccessDataExplorer: false,
      canManageUsers: false,
      marketplaceDetailTabs: ['overview', 'shipping', 'documents']
    }
  },
  [TENANT_TYPES.HALILAB]: {
    subdomain: 'halilab',
    name: 'HaliLab',
    defaultRoute: '/halilab/dashboard',
    allowedCategories: ['customer', 'platform_user', 'halilab', 'halilab_user'],
    theme: {
      primary: '#7c3aed', // Violet-600
      secondary: '#8b5cf6', // Violet-500
      accent: '#a78bfa', // Violet-400
      logo: '/assets/halilab-logo-purple.svg',
      favicon: '/icons/halilab-favicon.png',
      brandName: 'HaliLab',
      tagline: 'Innovation Hub'
    },
    features: {
      canViewOwnData: true,
      canEditOwnData: true,
      canAccessPrototypes: true,
      canAccessPOCTools: true,
      canAccessExperiments: true,
      canSubmitFeedback: true,
      canAccessDataExplorer: false,
      canManageUsers: false
    }
  },
  [TENANT_TYPES.HALIFUND]: {
    subdomain: 'halifund',
    name: 'HaliFund',
    defaultRoute: '/halifund/dashboard',
    allowedCategories: ['investor', 'funder', 'halifund', 'halifund_user'],
    theme: {
      primary: '#10B981', // Green
      secondary: '#059669', // Dark Green
      accent: '#34D399', // Light Green
      logo: '/assets/halifund-logo.svg',
      brandName: 'HaliFund',
      brandNamePrefix: 'Hali',
      brandNameHighlight: 'Fund',
      tagline: 'powered by HaliSoft'
    },
    features: {
      canViewOwnData: true,
      canEditOwnData: false,
      canSubmitForApproval: false,
      canViewApprovalStatus: true,
      canAccessFactoring: true,
      canAccessSupplyChain: false,
      canAccessDataExplorer: false,
      canManageUsers: false,
      marketplaceDetailTabs: ['overview', 'shipping', 'documents', 'risk', 'financing']
    }
  }
};

/**
 * Détecte le type de tenant basé sur le route path
 * @param {string} pathname - Le pathname de l'URL (ex: /halitrade/dashboard)
 * @returns {string} - Le type de tenant (CUSTOMER, REFERRAL, PLATFORM, HALITRADE)
 */
// Memoize detectTenant to prevent excessive re-renders
let cachedPathname = null;

// Track the current route prefix for navigation purposes
let currentRoutePrefix = null;

export const getCurrentRoutePrefix = () => currentRoutePrefix;

export const detectTenant = (pathname = (isBrowserEnvironment ? window.location.pathname : '')) => {
  const currentPath = pathname || '';

  // Return cached value if pathname hasn't changed
  if (cachedPathname === currentPath && cachedTenantType !== null) {
    return cachedTenantType;
  }

  cachedPathname = currentPath;
  const matchesPathPrefix = (pathPrefix) =>
    currentPath === pathPrefix || currentPath.startsWith(`${pathPrefix}/`);

  // Track the route prefix for navigation (investor/buyer/customer use their own prefixes)
  if (currentPath.startsWith('/investor')) {
    currentRoutePrefix = '/investor';
  } else if (currentPath.startsWith('/halifund')) {
    currentRoutePrefix = '/halifund';
  } else if (currentPath.startsWith('/buyer')) {
    currentRoutePrefix = '/buyer';
  } else if (currentPath.startsWith('/customer')) {
    currentRoutePrefix = '/customer';
  } else if (currentPath.startsWith('/haliflow')) {
    currentRoutePrefix = '/haliflow';
  } else if (currentPath.startsWith('/halitrade')) {
    currentRoutePrefix = '/halitrade';
  } else if (currentPath.startsWith('/halilab')) {
    currentRoutePrefix = '/halilab';
  } else if (currentPath.startsWith('/referral')) {
    currentRoutePrefix = '/referral';
  } else if (currentPath.startsWith('/platform')) {
    currentRoutePrefix = '/platform';
  } else if (currentPath.startsWith('/haliagent')) {
    currentRoutePrefix = '/haliagent';
  }

  // Route-based tenant detection
  if (currentPath.startsWith('/investor') || currentPath.startsWith('/halifund')) {
    cachedTenantType = TENANT_TYPES.HALIFUND;
    return rememberTenant(cachedTenantType);
  }

  // Note: buyer/customer use HaliFlow tenant but have their own route prefixes
  if (currentPath.startsWith('/buyer') || currentPath.startsWith('/customer')) {
    cachedTenantType = TENANT_TYPES.CUSTOMER;
    return rememberTenant(cachedTenantType);
  }

  if (currentPath.startsWith('/haliflow')) {
    cachedTenantType = TENANT_TYPES.CUSTOMER;
    return rememberTenant(cachedTenantType);
  }

  if (currentPath.startsWith('/halitrade')) {
    cachedTenantType = TENANT_TYPES.HALITRADE;
    return rememberTenant(cachedTenantType);
  }

  if (currentPath.startsWith('/haliagent')) {
    cachedTenantType = TENANT_TYPES.HALIAGENT;
    return rememberTenant(cachedTenantType);
  }

  if (currentPath.startsWith('/halilab')) {
    cachedTenantType = TENANT_TYPES.HALILAB;
    return rememberTenant(cachedTenantType);
  }

  if (currentPath.startsWith('/halifund')) {
    cachedTenantType = TENANT_TYPES.HALIFUND;
    return rememberTenant(cachedTenantType);
  }

  if (currentPath.startsWith('/referral')) {
    cachedTenantType = TENANT_TYPES.REFERRAL;
    return rememberTenant(cachedTenantType);
  }

  if (currentPath.startsWith('/platform')) {
    cachedTenantType = TENANT_TYPES.PLATFORM;
    return rememberTenant(cachedTenantType);
  }

  if (PLATFORM_PATH_ALIASES.some(matchesPathPrefix)) {
    cachedTenantType = TENANT_TYPES.PLATFORM;
    return rememberTenant(cachedTenantType);
  }

  // Check sessionStorage for remembered tenant
  const storedTenant = getRememberedTenant();
  if (storedTenant) {
    cachedTenantType = storedTenant;
    return storedTenant;
  }

  // For public routes (landing, about, etc.), check hostname in production
  const currentHostname = isBrowserEnvironment ? getHostname() : '';
  const baseDomain = isBrowserEnvironment ? getPrimaryDomain() : '';
  const matchesSubdomain = (subdomain) =>
    Boolean(subdomain && baseDomain && currentHostname.endsWith(`${subdomain}.${baseDomain}`));

  if (matchesSubdomain('customer') || matchesSubdomain('haliflow')) {
    cachedTenantType = TENANT_TYPES.CUSTOMER;
    return cachedTenantType;
  }
  if (matchesSubdomain('referral')) {
    cachedTenantType = TENANT_TYPES.REFERRAL;
    return cachedTenantType;
  }
  if (matchesSubdomain('platform') || matchesSubdomain('app')) {
    cachedTenantType = TENANT_TYPES.PLATFORM;
    return cachedTenantType;
  }
  if (matchesSubdomain('halitrade')) {
    cachedTenantType = TENANT_TYPES.HALITRADE;
    return cachedTenantType;
  }
  if (matchesSubdomain('halilab')) {
    cachedTenantType = TENANT_TYPES.HALILAB;
    return cachedTenantType;
  }
  if (matchesSubdomain('halifund')) {
    cachedTenantType = TENANT_TYPES.HALIFUND;
    return cachedTenantType;
  }

  // Default to PLATFORM for development/unknown routes
  cachedTenantType = TENANT_TYPES.PLATFORM;
  return rememberTenant(cachedTenantType);
};

/**
 * Obtient la configuration du tenant actuel
 * @returns {Object} - Configuration du tenant
 */
export const getTenantConfig = () => {
  const tenantType = detectTenant();
  return {
    type: tenantType,
    ...TENANT_CONFIG[tenantType]
  };
};

/**
 * Vérifie si l'utilisateur a accès au tenant actuel
 * @param {Object} user - L'utilisateur connecté
 * @returns {boolean} - True si l'utilisateur a accès
 */
export const hasAccessToTenant = (user) => {
  if (!user) return false;

  const config = getTenantConfig();
  const normalized = deriveUserCategory(user);
  const aliasList = CATEGORY_ALIASES[normalized] || [normalized];
  if (aliasList.some((alias) => config.allowedCategories.includes(alias))) return true;
  if (user.category && config.allowedCategories.includes(user.category)) return true;
  if (user.role && config.allowedCategories.includes(user.role)) return true;
  return false;
};

/**
 * Normalize user category/role/userType into one of the internal tenant categories.
 * @param {Object} user
 * @returns {'customer'|'referral'|'platform_user'}
 */
const deriveUserCategory = (user) => {
  if (!user) return TENANT_TYPES.CUSTOMER;
  const rawCategory = String(user.category || '').trim().toLowerCase();
  const normalizedCategory = rawCategory.replace(/[\s-]+/g, '_');
  const rawRole = String(user.role || '').trim().toLowerCase();
  const rawUserType = String(user.userType || '').trim().toLowerCase();
  const rawTenant = String(user.tenant || '').trim().toLowerCase();

  if (
    rawTenant === 'haliagent' ||
    HALIAGENT_ALIASES.has(rawCategory) ||
    HALIAGENT_ALIASES.has(rawUserType) ||
    normalizedCategory === 'haliagent' ||
    rawRole.includes('haliagent') ||
    rawRole.includes('agent')
  ) {
    return TENANT_TYPES.HALIAGENT;
  }

  if (
    rawTenant === 'halitrade' ||
    HALITRADE_ALIASES.has(rawCategory) ||
    HALITRADE_ALIASES.has(rawUserType) ||
    normalizedCategory === 'halitrade' ||
    rawRole.includes('halitrade')
  ) {
    return TENANT_TYPES.HALITRADE;
  }

  if (
    rawTenant === 'halilab' ||
    HALILAB_ALIASES.has(rawCategory) ||
    HALILAB_ALIASES.has(rawUserType) ||
    normalizedCategory === 'halilab' ||
    rawRole.includes('halilab')
  ) {
    return TENANT_TYPES.HALILAB;
  }

  if (
    rawTenant === 'halifund' ||
    HALIFUND_ALIASES.has(rawCategory) ||
    HALIFUND_ALIASES.has(rawUserType) ||
    normalizedCategory === 'halifund' ||
    rawRole.includes('investor') ||
    rawRole.includes('funder')
  ) {
    return TENANT_TYPES.HALIFUND;
  }

  if (
    normalizedCategory === 'platform_user' ||
    normalizedCategory === 'platform' ||
    normalizedCategory === 'platform_admin' ||
    rawUserType === 'platform' ||
    rawUserType === 'platform_user' ||
    rawRole.includes('platform')
  ) {
    return TENANT_TYPES.PLATFORM;
  }

  if (
    rawCategory === 'referral' ||
    REFERRAL_ALIASES.has(rawCategory) ||
    rawUserType === 'referral'
  ) {
    return TENANT_TYPES.REFERRAL;
  }

  return TENANT_TYPES.CUSTOMER;
};

/**
 * Redirige vers le bon tenant basé sur la catégorie de l'utilisateur
 * @param {Object} user - L'utilisateur connecté
 */
export const redirectToCorrectTenant = (user) => {
  if (!user) return;

  const currentTenant = detectTenant();
  const normalizedCategory = deriveUserCategory(user);
  const hasCategoryHints =
    Boolean(user.category && String(user.category).trim()) ||
    Boolean(user.role && String(user.role).trim()) ||
    Boolean(user.userType && String(user.userType).trim());

  if (!hasCategoryHints && normalizedCategory === TENANT_TYPES.CUSTOMER) {
    return;
  }

  let targetTenant = TENANT_TYPES.CUSTOMER;

  if (normalizedCategory === TENANT_TYPES.PLATFORM) {
    targetTenant = TENANT_TYPES.PLATFORM;
  } else if (normalizedCategory === TENANT_TYPES.REFERRAL) {
    targetTenant = TENANT_TYPES.REFERRAL;
  } else if (normalizedCategory === TENANT_TYPES.HALITRADE) {
    targetTenant = TENANT_TYPES.HALITRADE;
  } else if (normalizedCategory === TENANT_TYPES.HALIAGENT) {
    targetTenant = TENANT_TYPES.HALIAGENT;
  } else if (normalizedCategory === TENANT_TYPES.HALILAB) {
    targetTenant = TENANT_TYPES.HALILAB;
  } else if (normalizedCategory === TENANT_TYPES.HALIFUND) {
    targetTenant = TENANT_TYPES.HALIFUND;
  }

  const targetConfig = TENANT_CONFIG[targetTenant];
  const protocol = window.location.protocol;
  const isLocalhost = window.location.hostname === 'localhost' ||
    window.location.hostname.startsWith('127.0.0.1');

  // Store the tenant in sessionStorage to persist across navigation
  rememberTenant(targetTenant);

  // Check if we need to redirect
  let needsRedirect = false;

  if (isLocalhost) {
    // En développement, vérifier seulement le path
    const currentPath = window.location.pathname;
    const targetPrefix = targetConfig.defaultRoute.split('/').slice(0, 2).join('/'); // e.g., '/haliflow'
    const currentPrefix = currentPath.split('/').slice(0, 2).join('/');
    needsRedirect = currentPrefix !== targetPrefix;
  } else {
    // En production, vérifier à la fois le path ET le sous-domaine
    const currentSubdomain = window.location.hostname.split('.')[0] + '.' + window.location.hostname.split('.')[1];
    const expectedSubdomain = targetConfig.subdomain;

    // Si le sous-domaine ne correspond pas OU si le path ne correspond pas
    if (currentSubdomain !== expectedSubdomain || currentTenant !== targetTenant) {
      needsRedirect = true;
    }
  }

  // Si redirection nécessaire
  if (needsRedirect) {
    if (isLocalhost) {
      window.location.href = `${protocol}//${window.location.host}${targetConfig.defaultRoute}`;
    } else {
      // En production, rediriger vers le bon sous-domaine
      const baseDomain = getPrimaryDomain();
      if (baseDomain) {
        window.location.href = `${protocol}//${targetConfig.subdomain}.${baseDomain}${targetConfig.defaultRoute}`;
      }
    }
  }
};

/**
 * Get tenant-prefixed path for navigation
 * @param {string} path - The relative path (e.g., '/company', '/dashboard')
 * @param {string} tenantType - Optional tenant type, uses current if not provided
 * @returns {string} - Full tenant-prefixed path (e.g., '/halitrade/company')
 */
export const getTenantPath = (path, tenantType = null) => {
  const tenant = tenantType || detectTenant();
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;

  // First, check if we have a tracked route prefix (for investor/buyer special cases)
  // This ensures navigation stays within the correct route context
  if (currentRoutePrefix) {
    return `${currentRoutePrefix}/${cleanPath}`;
  }

  // Map tenant types to route prefixes
  // Platform uses /platform when on platform routes, otherwise /haliflow for shared
  const isPlatformRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/platform');
  const isCustomerRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/customer');
  const prefixes = {
    [TENANT_TYPES.CUSTOMER]: isCustomerRoute ? '/customer' : '/haliflow',
    [TENANT_TYPES.HALITRADE]: '/halitrade',
    [TENANT_TYPES.HALIAGENT]: '/haliagent',
    [TENANT_TYPES.HALILAB]: '/halilab',
    [TENANT_TYPES.HALIFUND]: '/halifund',
    [TENANT_TYPES.PLATFORM]: isPlatformRoute ? '/platform' : '/haliflow',
    [TENANT_TYPES.REFERRAL]: '/referral'
  };

  const prefix = prefixes[tenant] || '/haliflow';
  return `${prefix}/${cleanPath}`;
};

/**
 * Hook React pour obtenir la configuration du tenant
 */
export const useTenant = () => {
  const config = getTenantConfig();

  return {
    tenantType: config.type,
    tenantName: config.name,
    defaultRoute: config.defaultRoute,
    theme: config.theme,
    features: config.features,
    config,
    // Utility function to get tenant-prefixed paths
    getTenantPath: (path) => getTenantPath(path, config.type)
  };
};

export const resolveUserTenant = (user) => deriveUserCategory(user);

export const _resetCache = () => {
  cachedTenantType = null;
  cachedPathname = null;
};

export default {
  TENANT_TYPES,
  TENANT_CONFIG,
  detectTenant,
  getTenantConfig,
  getTenantPath,
  hasAccessToTenant,
  redirectToCorrectTenant,
  resolveUserTenant,
  useTenant,
  _resetCache
};
